import type { AccountsWidgetAdapter } from '../AccountsWidgetAdapter';
import type { ChannelSlice } from '../ChannelSlice';
import type { StickyFilterSlice } from '../StickyFilterSlice';
import { makeHeaderElements } from '../WidgetModel';
import type { WidgetHeaderElement } from '../WidgetModel';
import { COMMON_HEADER_ELEMENTS, STANDALONE_COMMON_HEADER_ELEMENTS } from '../WidgetModel/impl';
import type { WidgetTabSlice } from '../WidgetTabSlice';
import { computed, signal } from '@preact/signals-react';
import type { ReadonlySignal, Signal } from '@preact/signals-react';
import { EmptyArray } from '@thinkalpha/common/util/iterators.js';
import type { ColumnTemplate } from '@thinkalpha/ui-types/contracts/column-template';
import { t } from 'i18next';
import { optional } from 'inversify';
import { Observable } from 'rxjs';
import type { TableStatus } from 'src/components/table-view/model';
import type { GridSettingsNavigationTarget } from 'src/features/grid-settings/models/NavigationModel';
import { ReactiveInjectable, inject, injectable } from 'src/features/ioc';
import type { AccountsWidgetModel } from 'src/models/AccountsWidgetModel';
import { getColumnTemplateByIdQuery, getDefaultColumnTemplateQuery } from 'src/queries/columnTemplates';
import { generateWorkableColumnTemplateGridSettings } from 'src/routes/dashboard/widget-container/widget-settings-editor/context';
import type { WorkspaceService } from 'src/services/WorkspaceService';
import type { ReactBindings } from 'src/types/bindings';
import type { ConnectionStatusSignal } from 'src/types/EventBus';
import { EventBusBuilder, MergedEventBus } from 'src/types/EventBus/EventBus';

@injectable()
export class AccountsWidgetModelImpl extends ReactiveInjectable implements AccountsWidgetModel {
    #channelSlice: ChannelSlice;
    #widgetTabSlice: WidgetTabSlice;

    #accountIds: Signal<string[]> = signal([]);
    #platforms: Signal<string[]> = signal([]);
    #isAccountSelectionValid: Signal<boolean> = signal(true);
    #selectedColumnTemplate: Signal<ColumnTemplate | string | null> = signal(null);
    #isGridSettingsDialogOpen: Signal<boolean> = signal(false);
    #gridSettingsDialogTarget: Signal<GridSettingsNavigationTarget | null> = signal(null);
    #isColumnTemplateLibraryOpen: Signal<boolean> = signal(false);
    #tableRowCount: Signal<number> = signal(0);
    #tableRowCount$: Signal<Observable<number> | undefined> = signal(undefined);
    #tableTotalRowCount: Signal<number | null> = signal(null);
    #tableRowCountSubscription: { unsubscribe(): void } | null = null;
    #tableConnectionStatus: Signal<
        ConnectionStatusSignal['widget::connectionStatus'] extends ReadonlySignal<infer T> ? T : never
    > = signal({
        status: 'uninitialized' as TableStatus,
        progress: undefined,
        failureCount: 0,
    });
    #log: ReactBindings['Logger'];
    #resolveColumnTemplateRequestId = 0;
    #refetchCallback: (() => void) | null = null;

    headerElements!: WidgetHeaderElement[];

    eventBus!: AccountsWidgetModel['eventBus'];

    #buildEventBus(): AccountsWidgetModel['eventBus'] {
        return MergedEventBus.from(
            EventBusBuilder.create()
                // Accounts-specific events
                .addEvent('widget::instrumentChange', (_payload) => {
                    // Accounts widget doesn't use instrument
                })
                .addEvent('widget::updateSelectedAccountIds', (payload) => {
                    this.adapter.persist({ accountIds: payload.accountIds });
                    this.#accountIds.value = payload.accountIds;
                })
                .addEvent('widget::updateSelectedAccountId', (payload) => {
                    this.adapter.persist({ accountIds: [payload.accountId] });
                    this.#accountIds.value = [payload.accountId];
                })
                .addEvent('widget::setIsAccountSelectionValid', (payload) => {
                    this.#isAccountSelectionValid.value = payload.isValid;
                })
                .addEvent('widget::updateSelectedPlatforms', (payload) => {
                    this.adapter.persist({ platforms: payload.platformIds });
                    this.#platforms.value = payload.platformIds;
                })
                .addEvent('widget::updateConcreteTemplate', (payload) => {
                    this.adapter.persist({ columnTemplate: payload.columnTemplate });
                    this.#selectedColumnTemplate.value = payload.columnTemplate;
                })
                .addEvent('widget::selectColumnTemplate', (payload) => {
                    this.adapter.persist({ columnTemplate: payload.templateId });
                    this.#selectedColumnTemplate.value = payload.templateId;
                })
                .addEvent('widget::onColumnTemplateLibraryOpen', (payload) => {
                    this.#isColumnTemplateLibraryOpen.value = payload.isOpen;
                })
                // Accounts-specific signals
                .addSignal('widget::selectedAccountIds', () =>
                    computed(() => this.#accountIds.value ?? (EmptyArray as string[])),
                )
                .addSignal('widget::isAccountSelectionValid', () => this.#isAccountSelectionValid)
                .addSignal('widget::selectedPlatforms', () =>
                    computed(() => this.#platforms.value ?? (EmptyArray as string[])),
                )
                .addSignal('widget::connectionStatus', () => this.#tableConnectionStatus)
                .addSignal('widget::formattableGridSettings', () =>
                    computed(() => {
                        const columnTemplate = this.#selectedColumnTemplate.value;
                        if (!columnTemplate || typeof columnTemplate === 'string') {
                            return { settings: null, defaults: null };
                        }
                        const sanitized = generateWorkableColumnTemplateGridSettings(columnTemplate);
                        return { settings: sanitized, defaults: sanitized };
                    }),
                )
                .addSignal('widget::selectedColumnTemplate', () => this.#selectedColumnTemplate)
                .addEvent('widget::openGridSettingsDialog', (payload) => {
                    this.#isGridSettingsDialogOpen.value = payload.isOpen;
                    this.#gridSettingsDialogTarget.value =
                        payload.isOpen && (payload.targetTab !== undefined || payload.targetColumnId !== undefined)
                            ? { tab: payload.targetTab, columnId: payload.targetColumnId }
                            : null;
                })
                .addSignal('widget::isGridSettingsDialogOpen', () => this.#isGridSettingsDialogOpen)
                .addSignal('widget::gridSettingsDialogTarget', () => this.#gridSettingsDialogTarget)
                .addSignal('widget::isColumnTemplateLibraryOpen', () => this.#isColumnTemplateLibraryOpen)
                .addSignal('widget::tableRowCount', () => this.#tableRowCount)
                .addSignal('widget::tableRowCountObservable', () => this.#tableRowCount$)
                .addSignal('widget::tableTotalRowCount', () => this.#tableTotalRowCount)
                .addEvent('widget::connectionStatusRefetch', () => {
                    this.#refetchCallback?.();
                })
                .build(),
        )
            .merge(this.#channelSlice.eventBus)
            .merge(this.#widgetTabSlice.eventBus)
            .merge(this.#stickyFilterSlice.eventBus);
    }

    #stickyFilterSlice: StickyFilterSlice;

    constructor(
        @inject('AccountsWidgetAdapter') private adapter: AccountsWidgetAdapter,
        @inject('QueryClient') private queryClient: ReactBindings['QueryClient'],
        @inject('Logger') logger: ReactBindings['Logger'],
        @inject('ChannelSlice') channelSlice: ChannelSlice,
        @inject('WidgetTabSlice') widgetTabSlice: WidgetTabSlice,
        @inject('StickyFilterSlice') stickyFilterSlice: StickyFilterSlice,
        @inject('WorkspaceService') @optional() workspaceService?: WorkspaceService,
        @inject('TabId') @optional() tabId?: string,
    ) {
        // eslint-disable-next-line prefer-rest-params -- forwarding constructor args
        super(...arguments);
        this.#channelSlice = channelSlice;
        this.#widgetTabSlice = widgetTabSlice;
        this.#stickyFilterSlice = stickyFilterSlice;
        this.#log = logger.getSubLogger({ name: 'AccountsWidgetModel' });

        if (tabId && workspaceService) {
            workspaceService.registerWidget(tabId, this);
            this.disposableStack.defer(() => workspaceService.deregisterWidget(tabId));
        }

        this.eventBus = this.#buildEventBus();
    }

    /**
     * Resolves the column template from the widget state.
     * The widget.columnTemplate can be:
     * - null: fetch the default template for the key
     * - string: fetch the template by ID
     * - ColumnTemplate object: use it directly (concrete)
     */
    private async resolveColumnTemplate(incomingColumnTemplate: ColumnTemplate | string | null): Promise<void> {
        // Increment request ID to track the latest request and prevent stale data from overwriting newer data
        const requestId = ++this.#resolveColumnTemplateRequestId;

        try {
            if (incomingColumnTemplate === null) {
                // Fetch default template for Account
                const queryOptions = getDefaultColumnTemplateQuery('Account');
                const template = await this.queryClient.fetchQuery(queryOptions);

                // Check if this is still the latest request before updating state
                if (requestId !== this.#resolveColumnTemplateRequestId) return;

                this.#selectedColumnTemplate.value = generateWorkableColumnTemplateGridSettings(template ?? null);
            } else if (typeof incomingColumnTemplate === 'string') {
                // Fetch referenced template by ID
                const queryOptions = getColumnTemplateByIdQuery(incomingColumnTemplate);
                const template = await this.queryClient.fetchQuery(queryOptions);

                // Check if this is still the latest request before updating state
                if (requestId !== this.#resolveColumnTemplateRequestId) return;

                this.#selectedColumnTemplate.value = generateWorkableColumnTemplateGridSettings(template ?? null);
            } else {
                // Use the concrete template directly (synchronous, no race condition possible)
                this.#selectedColumnTemplate.value = generateWorkableColumnTemplateGridSettings(incomingColumnTemplate);
            }
        } catch (error) {
            // Only log error if this is still the latest request
            if (requestId === this.#resolveColumnTemplateRequestId) {
                this.#log.error('Failed to resolve column template', { error, incomingColumnTemplate });
            }
        }
    }

    selectAccount(accountId: string) {
        this.adapter.selectAccount(accountId);
    }

    updateTableConnectionStatus(connectionStatus: {
        status: TableStatus;
        progress: number | undefined;
        failureCount: number;
    }): void {
        this.#tableConnectionStatus.value = connectionStatus;
    }

    setTableRowCount$(tableRowCount$: Observable<number>): void {
        this.#tableRowCount$.value = tableRowCount$;

        // Clean up previous subscription
        this.#tableRowCountSubscription?.unsubscribe();

        // Subscribe to the observable and update the signal
        this.#tableRowCountSubscription = tableRowCount$.subscribe((count) => {
            this.#tableRowCount.value = count;
        });
    }

    init(tabId?: string) {
        // Always initialize slices — use a generated UUID for standalone mode so signals are available
        const effectiveTabId = tabId ?? crypto.randomUUID();
        this.#channelSlice.init(effectiveTabId);
        this.#widgetTabSlice.init(effectiveTabId);
        this.disposableStack.use(this.#channelSlice);
        this.disposableStack.use(this.#widgetTabSlice);

        if (!tabId) {
            (this.#widgetTabSlice.eventBus.getSignal('widget::name') as Signal<string>).value =
                t('widgets::accounts::name');
        }

        // Initialize StickyFilterSlice with the shared adapter
        this.#stickyFilterSlice.init(this.adapter);
        this.disposableStack.use(this.#stickyFilterSlice);

        // Sync current adapter state into signals
        this.#syncFromAdapter();

        // Resolve the column template (fetches default if null)
        void this.resolveColumnTemplate(this.adapter.getState().columnTemplate);

        this.headerElements = createAccountsWidgetHeaderElements({ isStandalone: !tabId });

        this.disposableStack.defer(() => {
            this.#tableRowCountSubscription?.unsubscribe();
        });
    }

    #syncFromAdapter() {
        const state = this.adapter.getState();
        this.#accountIds.value = state.accountIds;
        this.#platforms.value = state.platforms;
    }

    setRefetchCallback(fn: (() => void) | null): void {
        this.#refetchCallback = fn;
    }
}

export function createAccountsWidgetHeaderElements({ isStandalone = false }: { isStandalone?: boolean } = {}) {
    const baseElements = isStandalone ? STANDALONE_COMMON_HEADER_ELEMENTS : COMMON_HEADER_ELEMENTS;

    return makeHeaderElements<AccountsWidgetModel['eventBus']>([
        ...baseElements,
        { type: 'stickyFilter', position: 40, priority: 70 },
        { type: 'accounts', position: 60, priority: 80, selectType: 'multi' },
        { type: 'platforms', position: 65, priority: 60 },
        { type: 'columnTemplateMenu', position: 70, priority: 30, columnTemplateKey: 'Account' },
        { type: 'tableRowCount', position: 75, priority: 50 },
        { type: 'connectionStatus', position: 76, priority: 40 },
    ]);
}
